import { createContext } from "react";
import { IUnifyJob } from "../models/unifyjob";
import { ITechnician } from "../models/technician";
import { toast } from "react-toastify";
import agent from "../api/agent";
import { observable, action, computed, configure, runInAction } from "mobx";

configure({ enforceActions: "always" });

class UnifyJobStore {
  @observable unifyjobRegistry = new Map();
  @observable unifyjob: IUnifyJob | null = null;
  @observable unifyjobList: IUnifyJob[] = new Array();
  @observable unifyjobTech: IUnifyJob[] = new Array();
  @observable payrollcycleRegistry = new Map();
  @observable technicianRegistry = new Map();
  @observable technician: ITechnician | null = null;
  @observable loadingInitial = false;
  @observable submitting = false;
  @observable target = "";

  @computed get unifyjobnamesByDate() {
    return Array.from(this.unifyjobRegistry.values()).sort(
      (a, b) => Date.parse(a.startDate) - Date.parse(b.startDate)
    );
  }

  @computed get techniciansByName() {
    return Array.from(this.technicianRegistry.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  @computed get payrollcyclesByName() {
    return Array.from(this.payrollcycleRegistry.values()).sort(
      (a, b) =>
        a.startYear.localeCompare(b.startYear) ||
        a.endYear.localeCompare(b.endYear) ||
        a.payRun.localeCompare(b.payRun)
    );
  }

  @action loadUnifyJobTechs = async () => {
    this.loadingInitial = true;
    try {
      const unifyjob = await agent.UnifyJobs.list();
      runInAction("loading unifyjobs", () => {
        this.unifyjobTech = unifyjob;
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load UnifyJob List error", () => {
        this.loadingInitial = false;
      });
      toast.error("load UnifyJob List error");
    }
  };

  @action searchUnifyJobs = async (search: {
    tech: string;
    payRun: string;
    jobStartDate: string;
    jobEndDate: string;
    workNo: string;
  }) => {
    if (
      search.tech === "" &&
      search.payRun === "" &&
      search.jobStartDate === "" &&
      search.jobEndDate === "" &&
      search.workNo === ""
    ) {
      toast.info("Please select at least one search condition");
      return;
    } else if (search.jobStartDate !== "" && search.jobEndDate === "") {
      toast.info("Please select job end date");
      return;
    } else if (search.jobEndDate !== "" && search.jobStartDate === "") {
      toast.info("Please select job start date");
      return;
    }

    const params = new URLSearchParams();
    params.append("tech", search.tech);
    params.append("payRun", search.payRun);
    params.append("jobStartDate", search.jobStartDate);
    params.append("jobEndDate", search.jobEndDate);
    params.append("workNo", search.workNo);

    this.loadingInitial = true;
    try {
      const unifyjobs = await agent.UnifyJobs.search(params);
      this.unifyjobRegistry.clear();
      runInAction("loading unifyjobs", () => {
        unifyjobs.forEach((unifyjob) => {
          if (unifyjob.startDate !== null)
            unifyjob.startDate = unifyjob.startDate.substr(0, 10);
          this.unifyjobRegistry.set(unifyjob.id, unifyjob);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load UnifyJob List error", () => {
        this.loadingInitial = false;
      });
      toast.error("load UnifyJob List error");
    }
  };

  @action loadUnifyJob = async (id: string) => {
    let unifyjob = this.getUnifyJob(id);
    if (unifyjob) {
      this.unifyjob = unifyjob;
      return unifyjob;
    } else {
      this.loadingInitial = true;
      try {
        const unifyjob = await agent.UnifyJobs.details(id);
        runInAction("getting unifyjob", () => {
          if (unifyjob.startDate) {
            unifyjob.startDate = unifyjob.startDate.substr(0, 10);
          }
          this.unifyjob = unifyjob;
          this.loadingInitial = false;
        });
        return unifyjob;
      } catch (error) {
        runInAction("get UnifyJob error", () => {
          this.loadingInitial = false;
        });
        toast.error("get UnifyJob error");
      }
    }
  };

  getUnifyJob = (id: string) => {
    return this.unifyjobRegistry.get(Number(id));
  };

  @action clearUnifyJob = () => {
    this.unifyjob = null;
    this.unifyjobList.length = 0;
  };

  @action createUnifyJob = async (unifyjob: IUnifyJob[]) => {
    this.submitting = true;
    try {
      await agent.UnifyJobs.create(unifyjob);
      runInAction("create UnifyJob", () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create UnifyJob error", () => {
        this.submitting = false;
      });
      toast.error("create UnifyJob error");
    }
  };

  @action editUnifyJob = async (unifyjob: IUnifyJob) => {
    this.submitting = true;
    try {
      await agent.UnifyJobs.update(unifyjob);
      runInAction("editing unifyjob", () => {
        this.unifyjobRegistry.set(unifyjob.id, unifyjob);
        this.unifyjob = unifyjob;
        this.submitting = false;
      });
    } catch (error) {
      runInAction("edit UnifyJob error", () => {
        this.submitting = false;
      });
      toast.error("edit UnifyJob error");
    }
  };

  @action deleteUnifyJob = async (id: string) => {
    this.submitting = true;
    this.target = id;
    try {
      await agent.UnifyJobs.delete(id);
      runInAction("deleting UnifyJob", () => {
        this.unifyjobRegistry.delete(id);
        this.submitting = false;
        this.target = "";
      });
    } catch (error) {
      runInAction("delete UnifyJob error", () => {
        this.submitting = false;
        this.target = "";
      });
      toast.error("delete UnifyJob error");
    }
  };

  @action importUnifyJobs = async (data: any) => {
    var rowno = 0;
    try {
      this.unifyjobList.length = 0;
      data.forEach((unifyjobs: any) => {
        rowno = rowno + 1;
        const unifyjob: IUnifyJob = {
          id: "0",
          createdAt: "",
          updatedAt: "",
          financialYear: unifyjobs["Financial Year"],
          payRun: unifyjobs.PayRun,
          workOrderNumber: unifyjobs["Work Order ID"],
          tech: unifyjobs.Technician,
          startDate: unifyjobs["Start Date"],
          hours: unifyjobs.Hours,
          sorId: unifyjobs["SOR ID"],
          description: unifyjobs["Work Order Lines Description"],
          comment: unifyjobs["Artefact Comments"],
        };
        if (unifyjob.startDate == null) {
          unifyjob.startDate = "";
        } else {
          if (!isNaN(Number(unifyjob.startDate))) {
            let d = new Date(
              (parseInt(unifyjob.startDate) - 25569) * 86400 * 1000
            );
            const pad = (s: number) => {
              return s < 10 ? "0" + s : s;
            };
            unifyjob.startDate = [
              d.getFullYear(),
              pad(d.getMonth() + 1),
              pad(d.getDate()),
            ].join("-");
          } else {
            let parts = unifyjob.startDate.split("/");
            unifyjob.startDate =
              parts[2] +
              "-" +
              (parseInt(parts[1]) < 10 ? "0" : "") +
              parseInt(parts[1]) +
              "-" +
              (parseInt(parts[0]) < 10 ? "0" : "") +
              parseInt(parts[0]);
          }
          var regEx = /^\d{4}-\d{2}-\d{2}$/;

          if (
            unifyjob.startDate.match(regEx) === null ||
            Date.parse(unifyjob.startDate) > Date.now()
          ) {
            throw "Invalid date";
          }
        }
        this.unifyjobList.push(unifyjob);
      });
      toast.success(
        `${this.unifyjobList.length} records successfully imported`
      );
    } catch (error) {
      toast.error(`import excel error, row #${rowno} has data format problem.`);
    }
  };
  @action loadPayrollCycles = async () => {
    this.loadingInitial = true;
    try {
      const payrollcycles = await agent.PayrollCycles.list();
      runInAction("loading payroll cycles", () => {
        payrollcycles.forEach((payrollcycle) => {
          this.payrollcycleRegistry.set(
            payrollcycle.startYear +
              "-" +
              payrollcycle.endYear +
              "-" +
              payrollcycle.payRun,
            payrollcycle
          );
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load payroll cycles error", () => {
        this.loadingInitial = false;
      });
      toast.error("load payroll cycles error");
    }
  };
}

export default createContext(new UnifyJobStore());
